import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLocations } from '@/stores/locations';
import { currentMonthStart } from '@/lib/scheduling';

/**
 * Landing page for /schedule. Lists each location with quick links to the
 * current and previous month. Day 6 will add a richer overview (status badges
 * per month, conflict counts).
 */
export default function ScheduleLanding() {
  const status = useLocations((s) => s.status);
  const items = useLocations((s) => s.items);
  const load = useLocations((s) => s.load);

  useEffect(() => {
    if (status === 'idle') void load();
  }, [status, load]);

  const monthNow = currentMonthStart();
  const yyyymm = monthNow.slice(0, 7);

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Schedule</h1>
          <p className="text-sm text-muted-foreground">
            Pick a location to view or edit its monthly schedule.
          </p>
        </div>

        {status === 'loading' && (
          <p className="text-sm text-muted-foreground">Loading locations...</p>
        )}

        {status === 'loaded' && items.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No locations yet. Add one in the{' '}
            <Link to="/locations" className="underline">
              Locations
            </Link>{' '}
            tab first.
          </div>
        )}

        {status === 'loaded' && items.length > 0 && (
          <ul className="space-y-2">
            {items
              .filter((l) => l.status !== 'closed')
              .map((loc) => (
                <li
                  key={loc.id}
                  className="rounded-lg border border-border bg-card p-4 flex items-center justify-between gap-3 flex-wrap"
                >
                  <div className="space-y-0.5 min-w-0">
                    <h3 className="text-sm font-medium truncate">{loc.name}</h3>
                    <p className="text-xs text-muted-foreground">
                      {loc.address ?? '—'}
                    </p>
                  </div>
                  <Link
                    to={`/schedule/${loc.id}/${yyyymm}`}
                    className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
                  >
                    Open this month
                  </Link>
                </li>
              ))}
          </ul>
        )}
      </div>
    </div>
  );
}
